<template>
  <div class="xiaomi-accessories">
    <Breadcrumb
      :items="breadcrumb"
      class="xiaomi-accessories__breadcrumb"
    />
    <div class="xiaomi-accessories__stage">
      <div class="xiaomi-accessories__stage-content-section">
        <div class="xiaomi-accessories__stage-content">
          <CdnImage
            class="xiaomi-accessories__stage-logo"
            :src="stage.logo"
            :alt="stage.title"
          />
          <Heading
            class="xiaomi-accessories__stage-heading"
            font-weight="ultra"
            size="xxl"
            text-uppercase
            tag="span"
            align="right"
          >
            {{ stage.title }}
          </Heading>
          <p class="xiaomi-accessories__stage-content-text">
            {{ stage.copy }}
          </p>
        </div>
      </div>
    </div>
    <GridContainer>
      <GridColumn>
        <GridRow class="xiaomi-accessories__headline-container">
          <h1 class="xiaomi-accessories__headline text--center">Alle Xiaomi Zubehörprodukte</h1>
        </GridRow>
      </GridColumn>
    </GridContainer>

    <div class="xiaomi-accessories__results-count text--center">{{ offersCount }} Artikel</div>

    <GridContainer>
      <OfferTilesContainer
        v-if="hasOffers"
        :offers="offers.entries"
        class="xiaomi-accessories__results"
      />
      <div
        v-else
        class="xiaomi-accessories__results xiaomi-accessories__results--empty grid"
      >
        <div class="grid__item grid__item--12 text--center grid__item--spacing-2">
          Es wurden leider keine Produkte gefunden.
        </div>
      </div>
    </GridContainer>
  </div>
</template>

<script>
import { ref } from 'vue';
import Breadcrumb from '@/components/breadcrumb.vue';
import PageHeader from '@/mixins/page-header';
import PageTracking from '@/mixins/page-tracking';
import CdnImage from '@/components/cdn-image.vue';
import { getDiscount, getPrice } from '@i22/rocket';

import { GridRow, GridColumn, GridContainer, Heading } from '@i22-td-smarthome/component-library';
import OfferTilesContainer from '~/components/offer-tiles-container.vue';
import fetchOffers from '@/lib/goliath/offers';
import calculateLastModified from '~/lib/last-modified-offer';
import { Track } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';

const DISPLAYED_OFFER_TYPES = ['TANGIBLE', 'MIXED'];

export default {
  name: 'XiaomiZubehoer',
  components: {
    Breadcrumb,
    OfferTilesContainer,
    GridRow,
    GridColumn,
    GridContainer,
    Heading,
    CdnImage,
  },
  setup: () => {
    Track({ page_type: 'theme.landing' });

    getHeader({
      title: ref('Xiaomi Zubehör entdecken'),
      isIndexed: ref(false),
      description: ref(
        'Ob Fitnesstracker, Gaming Monitor oder Electric Scooter: Xiaomi bietet mehr als nur Smartphones. Jetzt das Xiaomi Zubehör entdecken & online bestellen!'
      ),
    });
  },
  async asyncData(context) {
    let offers;
    try {
      const fetchedOffers = await fetchOffers({
        grouped: true,
        filter: {
          listed: true,
          types: DISPLAYED_OFFER_TYPES,
          productAvailable: true,
          brandSlugs: ['xiaomi'],
          productCategoryPaths: ['zubehoer', 'wearables', 'tablets-und-zubehoer/tablets', 'tv/tv'],
        },
        sort: { field: 'ONETIME_PRICE', direction: 'DESC' },
        includeAggregations: false,
      });
      if (fetchedOffers) {
        offers = {
          ...fetchedOffers,
          entries: fetchedOffers.entries
            ?.map((offer) => {
              const price = getPrice(offer.prices, 'DEFAULT', 'ONETIME')?.value;
              const wasPrice = getPrice(offer.prices, 'WAS', 'ONETIME')?.value;
              const discount = getDiscount(price, wasPrice);
              const rebateDescription =
                !!offer.rebate?.description && offer.rebate.description !== 'no description';

              const hasBadge = offer.isNew || !!discount || rebateDescription;
              return {
                ...offer,
                hasBadge,
              };
            })
            .sort((a, b) => b.hasBadge - a.hasBadge),
        };
      }
      // When we are fetching the results from the server-side we are returning
      // a 404 status code when the result set is empty. So we are fully SEO
      // compliant.
      if (process.server && (!offers || offers.pagination.totalCount === 0)) {
        context.res.statusCode = 404;
      }
    } catch (e) {
      const status = (process.server && e.response && e.response.status) || 500;
      context.error({ statusCode: status, message: e.message });
    }
    if (process.server && offers) {
      context.res.setHeader(
        'last-modified',
        calculateLastModified(offers, new Date(context.$config.buildDate))
      );
    }

    return { offers };
  },
  data() {
    return {
      offers: null,
      breadcrumb: [
        {
          label: 'Xiaomi Zubehör',
          to: 'xiaomi-zubehoer',
        },
      ],
      stage: {
        title: 'Xiaomi Zubehör',
        copy: 'Entdecke unsere vielfältigen Xiaomi Zubehörprodukte vom Monitor bis hin zum Luftreiniger.',
        logo: '/content/content-images/landingpages/xiaomi/437810097feb05894fae44fc82c0e236/xiaomi-logo.png',
      },
    };
  },
  computed: {
    offersCount() {
      return this.offers.variantsCount;
    },
    pagination() {
      return this.offers.pagination;
    },
    hasOffers() {
      return this.pagination.totalCount > 0;
    },
  },
};
</script>

<style lang="scss">
@use 'assets/base' as *;

$mq-md-1280: 1280px;

.xiaomi-accessories {
  &__headline {
    margin: {
      top: $grid-medium-gutter;
      bottom: $grid-small-gutter;
    }
    &-container {
      justify-content: center;
    }
  }

  &__results-count {
    font-size: 1.75rem;
    margin-bottom: $grid-medium-gutter;
  }

  &__results--empty {
    margin-top: $grid-medium-gutter;
  }

  &__results {
    display: flex;
    overflow-x: hidden;
    margin-bottom: 120px;
  }

  .text {
    &--nowrap {
      white-space: nowrap;
    }
  }

  &__stage {
    position: relative;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: 30%;
    margin-bottom: 100px;
    background-image: url('/content/content-images/landingpages/xiaomi/87a1992669e951578b3b10daf8958430/xiaomi-zubehoer-s.jpg');

    @include mq(420px) {
      background-position: 41%;
    }

    @include mq($mq-sm) {
      height: 380px;
    }

    @include mq(768px) {
      background-image: url('/content/content-images/landingpages/xiaomi/1399a91d811757a27c2c82823131dd7c/xiaomi-zubehoer-l.jpg');
    }

    @include mq($mq-md) {
      height: 560px;
      background-position: 40%;
    }

    @include mq($mq-md-1280) {
      background-position: center;
    }
  }

  &__stage-heading {
    width: 100%;
    margin-bottom: $grid-base * 2;
    margin-top: $grid-base * 2;
    font-size: 36px;
    line-height: 42px;

    @include mq($mq-sm) {
      font-size: 30px;
      line-height: 36px;
    }

    @include mq($mq-md) {
      font-size: 56px;
      line-height: 62px;
    }
  }

  &__stage-logo {
    width: 60px;
    height: 60px;
    margin-bottom: $grid-base * 2;
    @include mq($mq-sm) {
      width: 95px;
      height: 95px;
    }
  }

  &__stage-content-section {
    max-width: 1464px;
    width: 100%;
    margin-right: auto;
    margin-left: auto;
    box-sizing: border-box;
    height: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-top: 24px;
    text-align: right;

    @include mq($mq-md) {
      padding-top: 126px;
    }
  }

  &__stage-content {
    padding: 24px;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    @include mq(420px) {
      max-width: 60%;
    }

    @include mq($mq-sm) {
      max-width: 420px;
    }

    @include mq($mq-md) {
      max-width: 580px;
    }

    &-text {
      width: 100%;
    }
  }
}
</style>
